import type { CrmOnboardingRun } from '../../types/crmOnboarding';

// The Pull-now result. Presentational, server-render tested.
//
// ⚠️⚠️ DEFERRED FIRST PULLS ARE NAMED, NEVER FOLDED INTO "SUCCEEDED". One run
// gives at most N never-pulled clubs their first pull; the rest wait for the
// next run. A result that only said "succeeded" would read as "some clubs
// silently didn't work", so the deferred clubs get their own block with the cap
// and the remedy beside them.

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

const names = (cs: Array<{ companyId: number; name: string | null }>) =>
  cs.map((c) => c.name ?? String(c.companyId)).join(', ');

export function PullResultView({ run }: { run: CrmOnboardingRun }) {
  const header = (
    <p className="text-xs text-slate-500">
      Pull started {fmt(run.startedAt)} by {run.triggeredByEmail}
      {run.finishedAt ? ` · finished ${fmt(run.finishedAt)}` : ''}
    </p>
  );

  if (run.status === 'running') {
    return (
      <div className="space-y-2" data-testid="pull-running">
        {header}
        <p className="text-sm text-slate-700">
          Pulling on the kyck-crm-sync Job. A club getting its first pull takes a few minutes on its own; this page
          refreshes itself.
        </p>
      </div>
    );
  }

  const p = run.pullResult;
  if (p === null) {
    return (
      <div className="space-y-2" data-testid="pull-no-result">
        {header}
        {run.error && <pre className="whitespace-pre-wrap text-sm text-red-700">{run.error}</pre>}
        {run.resultUnavailable && <p className="text-sm text-red-700">{run.resultUnavailable}</p>}
        {!run.error && !run.resultUnavailable && (
          <p className="text-sm text-red-700">The pull finished without a readable result.</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="pull-result">
      {header}
      {run.warning && <p className="text-sm font-semibold text-red-700">{run.warning}</p>}
      <p className="text-sm text-slate-800">
        {p.clubsWritten} of {p.clubsSeen} club(s) pulled cleanly, {p.windowFrom} → {p.windowTo}. {p.leadRows} lead
        row(s){p.salesRows === null ? '' : `, ${p.salesRows} sales row(s)`}.
      </p>

      {p.firstPulls.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-900">First pulls this run — {p.firstPulls.length}</h3>
          <ul className="text-sm text-slate-700">
            {p.firstPulls.map((c) => (
              <li key={c.companyId}>
                {c.name ?? c.companyId} — backfilled from {c.from}
              </li>
            ))}
          </ul>
        </div>
      )}

      {p.deferred.length > 0 && (
        <div className="rounded-md border-2 border-amber-400 bg-amber-50 p-3" data-testid="pull-deferred">
          <h3 className="text-sm font-semibold text-slate-900">
            Waiting for the next run — {p.deferred.length} club(s)
          </h3>
          <p className="text-sm text-slate-800">
            One run gives at most {p.maxFirstPulls} never-pulled clubs their first pull, so these did not fit:{' '}
            <span className="font-medium">{names(p.deferred)}</span>. Nothing is wrong with them. Press Pull now again
            once this run has finished, or leave them for the nightly sync.
          </p>
        </div>
      )}

      {p.excludedNoZone.length > 0 && (
        <p className="text-sm text-amber-800">
          Left out — no resolved timezone, so their day boundary is unknown: {names(p.excludedNoZone)}. Fix the
          timezone in GymSales, then register clubs again.
        </p>
      )}

      {p.clubs403.length > 0 && (
        <p className="text-sm text-slate-600">
          {p.clubs403.length} club(s) answered 403 (authenticated, no access) — a standing GymSales fact:{' '}
          {names(p.clubs403)}.
        </p>
      )}

      {p.failureCount > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-red-700">
            Failures — {p.failureCount}
            {p.failureCount > p.failures.length ? ` (first ${p.failures.length} shown)` : ''}
          </h3>
          <ul className="text-sm text-red-700">
            {p.failures.map((f, i) => (
              <li key={i}>
                [{f.scope}] {f.detail}
              </li>
            ))}
          </ul>
        </div>
      )}
      {run.error && <pre className="whitespace-pre-wrap text-sm text-red-700">{run.error}</pre>}
    </div>
  );
}
